import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Menu, Phone, Calendar } from 'lucide-react';
import { Button } from '../components/ui/button';
import {
  Sheet,
  SheetContent,
  SheetTrigger,
} from '../components/ui/sheet';

const navItems = [
  { label: 'Home', href: '#home' },
  { label: 'Highlights', href: '#highlights' },
  { label: 'Pricing', href: '#pricing' },
  { label: 'Floor Plan', href: '#floorplan' },
  { label: 'Amenities', href: '#amenities' },
  { label: 'Gallery', href: '#gallery' },
  { label: 'Location', href: '#location' },
];

export default function Navbar({ onEnquireClick }) {
  const [scrolled, setScrolled] = useState(false);
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleNavClick = (href) => {
    setIsOpen(false);
    const element = document.querySelector(href);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <motion.nav
      data-testid="navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 bg-white ${
        scrolled ? 'shadow-md' : 'shadow-sm'
      }`}
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.5 }}
    >
      <div className="max-w-[1400px] mx-auto px-3 sm:px-4 lg:px-6">
        <div className="flex items-center justify-between h-16 md:h-18 lg:h-20">
          {/* Logo - smaller on desktop */}
          <a 
            href="#home" 
            className="flex items-center"
            data-testid="navbar-logo"
            onClick={(e) => {
              e.preventDefault();
              handleNavClick('#home');
            }}
          >
            <img 
              src="/media/logo.webp" 
              alt="Sai Sun City by Paradise Group" 
              className="h-10 sm:h-11 md:h-12 lg:h-10 w-auto object-contain"
            />
          </a>

          {/* Desktop Navigation - no icons */}
          <div className="hidden lg:flex items-center gap-4 xl:gap-6">
            {navItems.map((item) => (
              <a
                key={item.label}
                href={item.href}
                data-testid={`nav-${item.label.toLowerCase().replace(' ', '-')}`}
                onClick={(e) => {
                  e.preventDefault();
                  handleNavClick(item.href);
                }}
                className="text-sm text-gray-600 hover:text-[#9A7B4F] transition-colors font-medium whitespace-nowrap"
              >
                {item.label}
              </a>
            ))}
          </div>

          {/* CTA Buttons - Desktop */}
          <div className="hidden lg:flex items-center gap-2">
            <Button
              onClick={onEnquireClick}
              className="bg-[#9A7B4F] hover:bg-[#8A6B3F] text-white rounded-none px-4 py-4 text-sm font-semibold flex items-center gap-2"
              data-testid="nav-schedule-btn"
            >
              <Calendar className="w-4 h-4" />
              Schedule Visit
            </Button>
            <a 
              href="tel:+919321795663" 
              className="flex items-center gap-2 text-gray-700 bg-gray-100 px-3 py-2 hover:bg-gray-200 transition-colors"
              data-testid="nav-phone"
            >
              <Phone className="w-4 h-4 text-[#9A7B4F]" />
              <span className="text-sm font-medium">+919321795663</span>
            </a>
          </div>

          {/* Mobile Menu */}
          <div className="lg:hidden flex items-center gap-2">
            <a 
              href="tel:+919321795663" 
              className="flex items-center justify-center w-9 h-9 bg-[#9A7B4F] text-white rounded-sm"
            >
              <Phone className="w-4 h-4" />
            </a>
            <Sheet open={isOpen} onOpenChange={setIsOpen}>
              <SheetTrigger asChild>
                <Button 
                  variant="ghost" 
                  size="icon"
                  data-testid="mobile-menu-btn"
                  className="text-gray-700 hover:text-[#9A7B4F] hover:bg-gray-100 w-9 h-9"
                >
                  <Menu className="w-5 h-5" />
                </Button>
              </SheetTrigger>
              <SheetContent 
                side="right" 
                className="w-[280px] bg-white border-l border-gray-200 p-0"
              >
                <div className="flex flex-col h-full">
                  <div className="p-4 border-b border-gray-100">
                    <img 
                      src="/media/logo.png" 
                      alt="Sai Sun City" 
                      className="h-10 w-auto object-contain"
                    />
                  </div>
                  <nav className="flex-1 py-2">
                    {navItems.map((item) => (
                      <a
                        key={item.label}
                        href={item.href}
                        onClick={(e) => {
                          e.preventDefault();
                          handleNavClick(item.href);
                        }}
                        className="block px-4 py-3 text-gray-600 hover:text-[#9A7B4F] hover:bg-[#9A7B4F]/5 transition-colors font-medium text-sm"
                      >
                        {item.label}
                      </a>
                    ))}
                  </nav>
                  <div className="p-4 border-t border-gray-100 space-y-3">
                    <Button
                      onClick={() => {
                        setIsOpen(false);
                        onEnquireClick();
                      }}
                      className="w-full bg-[#9A7B4F] hover:bg-[#8A6B3F] text-white rounded-none font-semibold py-4 text-sm"
                    >
                      Schedule Visit
                    </Button>
                    <a 
                      href="tel:+919321795663" 
                      className="flex items-center justify-center gap-2 text-[#9A7B4F] py-2 font-medium text-sm"
                    >
                      <Phone className="w-4 h-4" />
                      <span>+91 93217 95663</span>
                    </a>
                  </div>
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </div>

      </div>
    </motion.nav>
  );
}
